import { useMemo, useState } from 'react';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import { CalendarPlus, ChevronLeft, ChevronRight, Footprints, RefreshCw, Wallet } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api/queryClient';
import { useAvailabilityView } from '@/api/views';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { buildHeatmap, type HeatmapCell } from '@/domain/queueEngine';
import { hourToIso } from '@/domain/depositWindow';
import { overlapsFirm } from '@atink/domain';
import { seatWalkInNow, draftWalkInCustomer } from '@/domain/walkInFlow';
import { checkInBooking } from '@/domain/sessionFlow';
import { simTimeFull, simTimeShort } from '@/utils/format';
import ArtistAvailabilityHeatmap from '@/components/queue/ArtistAvailabilityHeatmap';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import DepositLinkModal from '@/components/DepositLinkModal';
import WaiverModal from '@/components/WaiverModal';
import type { Booking, CustomerRecord, Studio } from '@/domain/types';

interface Props {
  studio: Studio;
}

interface PinnedSlot {
  artistId: string;
  startIso: string;
  endIso: string;
}

/** Minutes spanned by a pinned slot, floored at a 15-minute sitting. */
function slotMinutes(slot: PinnedSlot): number {
  return Math.max(15, Math.round((new Date(slot.endIso).getTime() - new Date(slot.startIso).getTime()) / 60000));
}

/**
 * Gap-first walk-in intake for the studio front desk (Diagram 07). Capture the
 * person's contact (phone required, saved for the SMS thread), read the SAME
 * shared availability heatmap the deposit/booking surfaces use, pin a slot, and
 * the action follows the picked date:
 *  - a gap TODAY -> "Seat now": create the booking now via seatWalkInNow (NO
 *    deposit; they pay in full at checkout) and route into the waiver + check-in
 *    step.
 *  - TOMORROW onward -> "Book + take deposit": resolve the walk-in customer, then
 *    open the shared DepositLinkModal grounded to the picked slot so the floor
 *    sets the deposit amount + method (no fixed deposit is taken silently).
 */
function WalkInIntakeInner({ studio }: Props) {
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const customersOnFile = useBrainStore((s) => s.customers);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const pushNotification = useUIStore((s) => s.pushNotification);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [want, setWant] = useState('');

  const [weekStartIso, setWeekStartIso] = useState(() => startOfDay(new Date(clockIso)).toISOString());
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [updatedIso, setUpdatedIso] = useState(clockIso);
  const [selection, setSelection] = useState<{ artistId: string; dayIso: string } | null>(null);
  const [pinnedSlot, setPinnedSlot] = useState<PinnedSlot | null>(null);

  // Today path: the seated booking we route into waiver + check-in.
  const [seating, setSeating] = useState(false);
  const [seatedBooking, setSeatedBooking] = useState<Booking | null>(null);
  const [waiverOpen, setWaiverOpen] = useState(false);
  // Future path: the resolved walk-in customer the deposit modal attaches to —
  // `isNew` = a DRAFT not yet on file, created inside the deposit's intent.
  const [depositCustomer, setDepositCustomer] = useState<{ customer: CustomerRecord; isNew: boolean } | null>(null);
  const [depositOpen, setDepositOpen] = useState(false);

  // Live-read availability for the heatmap's one-week window (tv-1vh.10) —
  // the only other bookings usage in this screen is the seat/deposit flows,
  // which mutate brainStore directly and don't read the collection back.
  const weekEndIso = useMemo(() => addDays(new Date(weekStartIso), 7).toISOString(), [weekStartIso]);
  const { data: availability, refetch: refetchAvailability } = useAvailabilityView(
    studio.id,
    weekStartIso,
    weekEndIso,
  );
  const bookings = availability?.bookings ?? {};

  const heatmapRows = useMemo(
    () =>
      buildHeatmap({
        artists,
        bookings,
        nowIso: clockIso,
        studioIds: [studio.id],
        startIso: weekStartIso,
        days: 7,
        // L5 (q44j #3): a same-day walk-in can be with the apprentice (D10-18).
        includeApprentices: true,
      }),
    // refreshNonce is an intentional recompute trigger (re-reads live store state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artists, bookings, clockIso, studio.id, weekStartIso, refreshNonce],
  );

  const phoneValid = phone.trim().length > 0;
  const pickedIsToday = pinnedSlot ? isSameDay(new Date(pinnedSlot.startIso), new Date(clockIso)) : false;
  const pinnedArtist = pinnedSlot ? artists[pinnedSlot.artistId] : undefined;

  const weekStart = new Date(weekStartIso);
  const weekRangeLabel = `${format(weekStart, 'd MMM')} - ${format(addDays(weekStart, 6), 'd MMM')}`;
  const stepWeek = (deltaWeeks: number) => {
    setWeekStartIso(addDays(new Date(weekStartIso), deltaWeeks * 7).toISOString());
    setSelection(null);
    setPinnedSlot(null);
  };
  const onRefresh = () => {
    setRefreshNonce((n) => n + 1);
    setUpdatedIso(clockIso);
    void refetchAvailability();
  };

  const onSelectCell = (cell: HeatmapCell) => setSelection({ artistId: cell.artistId, dayIso: cell.dayIso });
  const onPickWindow = (w: { startHour: number; endHour: number }) => {
    if (!selection) return;
    const slot = {
      artistId: selection.artistId,
      startIso: hourToIso(selection.dayIso, w.startHour),
      endIso: hourToIso(selection.dayIso, w.endHour),
    };
    // The DB's exact double-book rule against the live availability read: never
    // pin a window a firm booking already holds (the write would be refused).
    if (overlapsFirm(slot, bookings)) {
      pushNotification({
        intensity: 'high',
        title: 'That slot is already booked',
        body: 'Pick another time.',
        ttlMs: 4000,
      });
      return;
    }
    setPinnedSlot(slot);
  };

  const onSeatNow = () => {
    if (!pinnedSlot || !phoneValid || seating) return;
    setSeating(true);
    seatWalkInNow({
      name,
      phone,
      email,
      designBrief: want,
      artistId: pinnedSlot.artistId,
      startIso: pinnedSlot.startIso,
      durationMin: slotMinutes(pinnedSlot),
      onFailed: () => setSeating(false),
      onConfirmed: (booking) => {
    setSeating(false);
    setSeatedBooking(booking);
    setWaiverOpen(true);
    pushNotification({
      intensity: 'high',
      title: 'Walk-in seated',
      body: `No deposit — ${name || 'the walk-in'} pays in full at checkout. Sign the waiver, then check them in.`,
      ttlMs: 4000,
    });
      },
    });
  };

  const onBookDeposit = () => {
    if (!pinnedSlot || !phoneValid) return;
    // Resolve WITHOUT writing: a match reuses the card on file; a new walk-in is a
    // draft the deposit's single intent creates (tv-2na.8.2).
    const next = draftWalkInCustomer({ name, phone, email }, studio.id);
    // Keep the same draft id across re-opens (a retry must re-send the same ids).
    setDepositCustomer((prev) =>
      prev && prev.isNew && next.isNew && prev.customer.phone === next.customer.phone
        ? { ...next, customer: { ...next.customer, id: prev.customer.id } }
        : next,
    );
    setDepositOpen(true);
  };

  const onCheckIn = () => {
    if (!seatedBooking) return;
    const result = checkInBooking(seatedBooking.id);
    if (!result.ok) {
      if (result.reason === 'waiver-unsigned') {
        pushNotification({
          intensity: 'high',
          title: 'Waiver not signed',
          body: 'Sign the medical waiver first, then check them in.',
          ttlMs: 4000,
        });
        setWaiverOpen(true);
      }
      return;
    }
    pushNotification({
      intensity: 'high',
      title: 'Checked in',
      body: 'Walk-in checked in — seat them when a chair is ready.',
      ttlMs: 4000,
    });
    setSeatedBooking(result.booking);
  };

  const inputClass =
    'w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:border-gold-bright focus:outline-none';

  return (
    <div className="space-y-5" data-testid="walkin-intake">
      <div>
        <h3 className="heading-serif text-base text-cream mb-1 inline-flex items-center gap-2">
          <Footprints size={16} className="text-gold-bright" /> New walk-in
        </h3>
        <p className="font-inter text-[11px] text-cream-soft">
          Take their details, find a gap, then seat them today or take a deposit for a future date.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <div className="panel p-4 space-y-3" data-testid="walkin-contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label htmlFor="walkin-name" className="label-small-caps mb-1 block">
              Name
            </label>
            <input
              id="walkin-name"
              data-testid="walkin-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Customer name (or 'walk-in')"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="walkin-phone" className="label-small-caps mb-1 block">
              Phone · required
            </label>
            <input
              id="walkin-phone"
              data-testid="walkin-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Saved on file for SMS"
              aria-required="true"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="walkin-email" className="label-small-caps mb-1 block">
              Email · optional
            </label>
            <input
              id="walkin-email"
              data-testid="walkin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Optional"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="walkin-want" className="label-small-caps mb-1 block">
              What they want
            </label>
            <input
              id="walkin-want"
              data-testid="walkin-want"
              value={want}
              onChange={(e) => setWant(e.target.value)}
              placeholder="e.g. small script, forearm"
              className={inputClass}
            />
          </div>
        </div>
        {!phoneValid ? (
          <p className="font-inter text-[11px] text-blood" data-testid="walkin-phone-hint">
            A phone number is required so we can text them the waiver, deposit link and reminders.
          </p>
        ) : null}
        </div>

        <div className="space-y-4" data-testid="walkin-availability">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex items-center rounded-md border border-gold-dim/30 overflow-hidden">
            <button
              type="button"
              onClick={() => stepWeek(-1)}
              className="px-2 py-1 text-cream-soft hover:text-gold-bright transition"
              aria-label="Previous week"
              data-testid="walkin-week-prev"
            >
              <ChevronLeft size={12} />
            </button>
            <span
              className="px-3 py-1 text-[10px] font-inter uppercase tracking-widest text-cream whitespace-nowrap"
              data-testid="walkin-week-range"
            >
              {weekRangeLabel}
            </span>
            <button
              type="button"
              onClick={() => stepWeek(1)}
              className="px-2 py-1 text-cream-soft hover:text-gold-bright transition"
              aria-label="Next week"
              data-testid="walkin-week-next"
            >
              <ChevronRight size={12} />
            </button>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            className="btn-quiet text-[10px]"
            data-testid="walkin-refresh"
            title="Re-check availability against current bookings"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest text-right">
          Updated {simTimeShort(updatedIso)}
        </p>
      </div>

      <ArtistAvailabilityHeatmap
        rows={heatmapRows}
        studios={studios}
        selected={selection}
        onSelect={onSelectCell}
        onClear={() => setSelection(null)}
      />

      {selection ? (
        <ArtistDaySchedule
          artistId={selection.artistId}
          dayIso={selection.dayIso}
          onPickWindow={onPickWindow}
        />
      ) : null}

      {pinnedSlot ? (
        <div
          className="rounded-lg border border-gold-bright/50 bg-gold/10 p-3"
          data-testid="walkin-pinned-slot"
        >
          <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright">
            Picked slot {pickedIsToday ? '· today' : '· future date'}
          </p>
          <p className="font-serif text-sm text-cream mt-1">
            {pinnedArtist?.displayName ?? pinnedSlot.artistId} · {simTimeFull(pinnedSlot.startIso)} –{' '}
            {simTimeShort(pinnedSlot.endIso)}
          </p>
          {pickedIsToday ? (
            <button
              type="button"
              onClick={onSeatNow}
              disabled={!phoneValid || seating}
              data-testid="walkin-seat-now"
              aria-busy={seating}
              className="btn-primary w-full mt-3 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Footprints size={13} /> Seat now · no deposit
            </button>
          ) : (
            <button
              type="button"
              onClick={onBookDeposit}
              disabled={!phoneValid}
              data-testid="walkin-book-deposit"
              className="btn-primary w-full mt-3 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Wallet size={13} /> Book + take deposit
            </button>
          )}
          {!phoneValid ? (
            <p className="font-inter text-[11px] text-blood mt-2">Add a phone number to proceed.</p>
          ) : null}
        </div>
      ) : null}

      {seatedBooking ? (
        <div
          className="rounded-lg border border-mint/40 bg-mint/10 p-3"
          data-testid="walkin-seated-step"
        >
          <p className="font-inter text-[10px] uppercase tracking-widest text-mint">
            Seated now · pays in full at checkout
          </p>
          <p className="font-serif text-sm text-cream mt-1">
            Waiver then check-in — the same floor flow.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={() => setWaiverOpen(true)}
              className="btn-ghost text-xs"
              data-testid="walkin-open-waiver"
            >
              Waiver
            </button>
            <button
              type="button"
              onClick={onCheckIn}
              className="btn-primary text-xs"
              data-testid="walkin-check-in"
            >
              <CalendarPlus size={12} /> Check in
            </button>
          </div>
        </div>
      ) : null}
        </div>
      </div>

      {seatedBooking ? (
        <WaiverModal open={waiverOpen} booking={seatedBooking} onClose={() => setWaiverOpen(false)} />
      ) : null}

      {depositCustomer ? (
        <DepositLinkModal
          open={depositOpen}
          customer={depositCustomer.customer}
          inShop
          // Once the draft has landed (intent ok) it is on file — no longer new.
          newCustomer={depositCustomer.isNew && !customersOnFile[depositCustomer.customer.id]}
          onClose={() => setDepositOpen(false)}
          discussedSlot={pinnedSlot ?? undefined}
          initialPieceName={want.trim() || undefined}
        />
      ) : null}
    </div>
  );
}

/**
 * Guarantees a QueryClient for `useAvailabilityView` even when this screen
 * mounts standalone (existing unit tests render it directly, without the app
 * root's `<QueryClientProvider>` from main.tsx). Nesting the same shared
 * `queryClient` singleton here is a no-op when an ancestor already supplies
 * it — same client instance, so no duplicate cache.
 */
export default function WalkInIntake(props: Props) {
  return (
    <QueryClientProvider client={queryClient}>
      <WalkInIntakeInner {...props} />
    </QueryClientProvider>
  );
}
